/**
 * The invoice register.
 *
 * Every figure here is a decimal string formatted for display. Nothing on this
 * page parses money into a number — not to sum it, not to compare it, not to
 * decide a colour. `balance` is computed by the database as `total - paid`, so
 * the row and the detail page cannot disagree.
 *
 * Drafts are visible to staff and invisible to clients. That is not a filter on
 * this page: the client's endpoint confines itself in the query, which is the
 * only place a rule like that can be relied on.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, CalendarDays, Plus, Receipt } from 'lucide-react';
import {
  Button,
  Can,
  PageHeader,
  ServerTable,
  StatusChip,
  api,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  isPastDate,
  formatMoney,
  type ServerColumn,
} from '@connect/web-shared';

import { INVOICE_STATUS_TONES } from '../../lib/tones';
import { NewInvoiceDialog } from './new-invoice-dialog';

export interface InvoiceRow {
  public_id: string;
  code: string;
  status: string;
  client_public_id: string;
  client_name: string;
  project_name: string | null;
  account_manager_name: string | null;
  issue_date: string | null;
  due_date: string | null;
  currency: string;
  subtotal: string;
  tax_amount: string;
  total: string;
  paid_amount: string;
  balance: string;
  line_count: number;
  created_at: string;
}

interface FinanceOptions {
  statuses: { value: string; label: string }[];
}

const ENDPOINT = '/finance/invoices';

export function InvoicesPage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [recentKey, setRecentKey] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ['finance', 'options'],
    queryFn: () => api.get<FinanceOptions>('/finance/options'),
    staleTime: 10 * 60_000,
  });

  const fetchRows = useMemo(() => createServerFetcher<InvoiceRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);

  const columns = useMemo<ServerColumn<InvoiceRow>[]>(
    () => [
      {
        field: 'code',
        header: 'Invoice',
        icon: <Receipt className="size-3" />,
        filter: 'text',
        width: 140,
        render: (row) => <span className="font-mono text-xs font-medium">{row.code}</span>,
      },
      {
        field: 'status',
        header: 'Status',
        filter: 'select',
        options: options.data?.statuses,
        width: 120,
        render: (row) => <StatusChip value={row.status} tones={INVOICE_STATUS_TONES} />,
      },
      {
        field: 'client_name',
        header: 'Client',
        icon: <Building2 className="size-3" />,
        filter: 'select',
        minWidth: 190,
      },
      { field: 'project_name', header: 'Project', filter: 'select', minWidth: 170 },
      {
        field: 'issue_date',
        header: 'Issued',
        icon: <CalendarDays className="size-3" />,
        filter: 'date',
        width: 125,
        format: (value) => formatDate(value as string | null),
      },
      {
        field: 'due_date',
        header: 'Due',
        filter: 'date',
        width: 125,
        render: (row) => {
          const overdue =
            row.due_date !== null &&
            Number(row.balance) > 0 &&
            row.status !== 'draft' &&
            isPastDate(row.due_date);
          return (
            <span className={overdue ? 'font-medium text-danger' : undefined}>
              {formatDate(row.due_date)}
            </span>
          );
        },
      },
      {
        field: 'total',
        header: 'Total',
        filter: 'number',
        numeric: true,
        width: 135,
        format: (value, row) => formatMoney(value as string, row.currency),
      },
      {
        field: 'paid_amount',
        header: 'Paid',
        numeric: true,
        width: 130,
        sortable: false,
        hiddenByDefault: true,
        format: (value, row) => formatMoney(value as string, row.currency),
      },
      {
        field: 'balance',
        header: 'Outstanding',
        numeric: true,
        width: 140,
        // Computed in the statement, so it is not in the sort whitelist.
        sortable: false,
        render: (row) => (
          <span className={Number(row.balance) > 0 ? 'font-medium text-body' : 'text-faint'}>
            {formatMoney(row.balance, row.currency)}
          </span>
        ),
      },
      {
        field: 'account_manager_name',
        header: 'Account manager',
        filter: 'select',
        width: 170,
        hiddenByDefault: true,
      },
      {
        field: 'line_count',
        header: 'Lines',
        numeric: true,
        width: 85,
        sortable: false,
        hiddenByDefault: true,
      },
    ],
    [options.data],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Invoices"
        description="What has been billed, and what is still owed."
        actions={
          <Can permission="finance.invoice.create">
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="size-3.5" />
              New invoice
            </Button>
          </Can>
        }
      />

      <ServerTable<InvoiceRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['finance', 'invoices']}
        stateKey="finance.invoices"
        searchPlaceholder="Search by invoice number, client or project"
        viewField="code"
        onView={(row) => navigate(`/invoices/${row.public_id}`)}
        recentKey={recentKey}
        rowDetailFields={(row) => [
          { label: 'Client', value: row.client_name },
          { label: 'Project', value: row.project_name },
          { label: 'Subtotal', value: formatMoney(row.subtotal, row.currency) },
          { label: 'Tax', value: formatMoney(row.tax_amount, row.currency) },
          { label: 'Total', value: formatMoney(row.total, row.currency) },
          { label: 'Paid', value: formatMoney(row.paid_amount, row.currency) },
          { label: 'Outstanding', value: formatMoney(row.balance, row.currency) },
          { label: 'Lines', value: String(row.line_count) },
        ]}
        emptyTitle="No invoices yet"
        emptyDescription="An invoice starts as a draft: pull approved time or expenses onto it, add anything by hand, then issue it."
        emptyAction={
          <Can permission="finance.invoice.create">
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="size-3.5" />
              Start a draft
            </Button>
          </Can>
        }
      />

      <NewInvoiceDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(publicId) => {
          setCreating(false);
          setRecentKey(publicId);
          // Straight to the draft: an empty invoice is not a thing anybody
          // wants to admire in a list, it is a thing to put lines on.
          navigate(`/invoices/${publicId}`);
        }}
      />
    </div>
  );
}
